import { useContext, useRef } from "react";
import { AbortError, getById, validationFailed, ValidationResult, validationSuccessful } from "sprint-common-web";

import { OriginatorsDataContext } from "../../../common/data/lookup-data-providers/OriginatorsDataProvider";
import { useDeliveryBucketsApi } from "../api/useDeliveryBucketsApi";
import { DeliveryBucketListingContext } from "../context/DeliveryBucketListingContext";
import { DeliveryBucketItem } from "../listing/DeliveryBucketItem";
import { DeliveryBucketAccessType } from "./common/DeliveryBucketAccessType";
import { CreateDeliveryBucketFormInputs } from "./CreateDeliveryBucketForm";

export const useCreateDeliveryBucketForm = () => {
    const {
        createKeyBasedDeliveryBucket,
        createRoleBasedDeliveryBucket,
        validateUniqueId: validateId
    } = useDeliveryBucketsApi();

    const { originators } = useContext(OriginatorsDataContext);
    const { addDeliveryBucketItem } = useContext(DeliveryBucketListingContext);

    const abortController = useRef<AbortController>(new AbortController());

    const validateUniqueId = async (uniqueId: string | undefined): Promise<ValidationResult> => {
        abortController.current.abort();
        abortController.current = new AbortController();

        if (uniqueId) {
            try {
                const response = await validateId(uniqueId, abortController.current.signal);

                if (!response.isValid) {
                    return validationFailed(response.failures);
                }
            } catch (error) {
                if (error instanceof AbortError) {
                    return validationSuccessful();
                }

                throw error;
            }
        }

        return validationSuccessful();
    };

    const addDeliveryBucket = async ({
        accessType,
        uniqueId,
        originatorId,
        externalRoleArn,
        externalId,
        useDedicatedStorage
    }: CreateDeliveryBucketFormInputs) => {
        const originator = getById(originators, originatorId);

        switch (Number(accessType)) {
            case DeliveryBucketAccessType.Key: {
                const response = await createKeyBasedDeliveryBucket({ uniqueId, originatorId, useDedicatedStorage });

                const deliveryBucketItem: DeliveryBucketItem = {
                    deliveryBucketId: response.deliveryBucketId,
                    accessType: Number(accessType),
                    uniqueId,
                    originator: originator.name,
                    originatorId: originator.id,
                    isActive: true,
                    bucketName: response.bucketName,
                    resources: {
                        keyAge: "-"
                    },
                    isModified: true
                };

                addDeliveryBucketItem(deliveryBucketItem);

                break;
            }
            case DeliveryBucketAccessType.Role: {
                const response = await createRoleBasedDeliveryBucket({
                    uniqueId,
                    originatorId,
                    externalRoleArn,
                    externalId,
                    useDedicatedStorage
                });

                const deliveryBucketItem: DeliveryBucketItem = {
                    deliveryBucketId: response.deliveryBucketId,
                    accessType: Number(accessType),
                    uniqueId,
                    originator: originator.name,
                    originatorId: originator.id,
                    isActive: true,
                    bucketName: response.bucketName,
                    resources: {
                        roleArn: response.roleArn,
                        roleName: response.roleName
                    },
                    isModified: true
                };

                addDeliveryBucketItem(deliveryBucketItem);

                break;
            }
            default:
                throw new Error("Incorrect access type for delivery bucket");
        }
    };

    return {
        validateUniqueId,
        addDeliveryBucket
    };
};
